import React, { useMemo } from "react";
import styled from "@emotion/styled";
import moment from "moment";

import MapConfig from "services/map/MapConfig";
import ParkkiTheme from "themes/ParkkiTheme";
import { Box, IconButton } from "@mui/material";
import { FormattedMessage } from "react-intl";
import CommonUtils from "services/common/utils";
import { NightlightOutlined } from "@mui/icons-material";

const WIDTH_PER_CHAR = 24;
const Container = styled.div`
  min-width: 48px;
  min-height: 40px;
  width: ${(props) =>
    props.maxDigits > 0 ? `${props.maxDigits * WIDTH_PER_CHAR}px` : "auto"};
  max-width: 85%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  font-family: "Led Counter";
  font-size: 40px;
  padding-top: 12px;
  padding-bottom: 4px;
  padding-right: 18px;
  padding-left: 20px;
  margin: 0px;
  border: ${(props) => `3px solid ${props.selected ? "white" : "black"}`};
  color: ${(props) => props.color};
  background-color: #2d2d39;

  & > svg {
    margin-right: ${ParkkiTheme.spacings.teeny};
  }
`;

const LabelContainer = styled.span`
  width: 100%;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
`;

const StyledLabel = styled.span`
  display: flex;
`;

const AutoScrollingLabel = styled(StyledLabel)`
  display: flex;
  @keyframes translate-x-animation {
    from {
      -moz-transform: translateX(100%);
      -webkit-transform: translateX(100%);
      transform: translateX(100%);
    }
    to {
      -moz-transform: translateX(-100%);
      -webkit-transform: translateX(-100%);
      transform: translateX(-100%);
    }
  }

  /* for Firefox */
  @-moz-keyframes translate-x-animation {
    from {
      -moz-transform: translateX(100%);
    }
    to {
      -moz-transform: translateX(-100%);
    }
  }

  /* for Chrome */
  @-webkit-keyframes translate-x-animation {
    from {
      -webkit-transform: translateX(100%);
    }
    to {
      -webkit-transform: translateX(-100%);
    }
  }

  -moz-transform: translateX(100%);
  -webkit-transform: translateX(100%);
  transform: translateX(100%);

  -moz-animation: translate-x-animation 13s linear infinite;
  -webkit-animation: translate-x-animation 13s linear infinite;
  animation: translate-x-animation 13s linear infinite;
`;

function Display({ display }) {
  const color = useMemo(() => {
    const apiColor = display?.meta_data?.color || "default";

    return (
      MapConfig?.iot?.display?.color?.[apiColor] ||
      MapConfig?.iot?.display?.color?.default
    );
  }, [display]);

  const scrolling = useMemo(() => {
    if (!display?.meta_data?.scrolling || !display?.meta_data?.max_digits) {
      return false;
    }

    let labelWidth = display?.meta_data?.computed_value?.length || 0;

    if (display?.meta_data?.has_symbol) {
      labelWidth += 1;
    }

    return labelWidth > display?.meta_data?.max_digits;
  }, [display]);

  const LabelComponent = useMemo(() => {
    if (scrolling) {
      return AutoScrollingLabel;
    }

    return StyledLabel;
  }, [scrolling]);

  const label = useMemo(() => {
    if (display.status === "STAND_BY") {
      return "";
    }
    if (display?.meta_data?.has_symbol) {
      const symbolConfig = display?.meta_data?.symbol_config || {};
      return Object.keys(symbolConfig).reduce((previous, currentKey, index) => {
        const previousValue = index === 0 ? "" : previous;

        const CurrentSymbol =
          MapConfig?.iot?.display?.symbol?.[symbolConfig?.[currentKey]];

        return [
          ...previousValue,
          <CurrentSymbol key={display.id} />,
          previous.replace(currentKey, ""),
        ];
      }, display?.meta_data?.computed_value);
    }

    return display?.meta_data?.computed_value;
  }, [display]);

  const maxDigits = useMemo(() => {
    return display?.meta_data?.max_digits || 0;
  }, [display]);

  const getColorFromDuration = (status, duration) => {
    if (status === "STAND_BY") {
      return ParkkiTheme.colors.disabledGrey;
    }
    // 10 minutes
    if (duration < 600) {
      return ParkkiTheme.colors.green;
    } else if (duration >= 600 && duration < 1800) {
      // 10 minutes >= DURATION < 30 minutes
      return ParkkiTheme.colors.orange;
    }
    // >= 30 minutes
    return ParkkiTheme.colors.red;
  };

  const status = useMemo(() => {
    const duration = moment().diff(
      moment(
        display?.last_received_value_date ?? display?.last_sent_value_date
      ),
      "seconds"
    );
    let message = (
      <>
        <IconButton size="small" disableRipple sx={{ cursor: "default" }}>
          <NightlightOutlined sx={{ width: "0.9rem", height: "0.9rem" }} />
        </IconButton>
        <FormattedMessage id="displaysScene.standbyMode" />
      </>
    );
    if (display.status === "RUNNING") {
      message = <FormattedMessage id="displaysScene.runningMode" />;
    }
    return (
      <Box
        sx={{
          fontSize: "0.9rem",
          color: getColorFromDuration(display.status, duration),
          cursor: "default",
        }}
      >
        {message} {CommonUtils.formatDuration(duration)}
      </Box>
    );
  }, [display]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 2,
        }}
      >
        <Container maxDigits={maxDigits} color={color}>
          <LabelContainer>
            <LabelComponent>{label}</LabelComponent>
          </LabelContainer>
        </Container>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "end",
          justifyContent: "end",
          p: 1,
          mt: 1,
        }}
      >
        {status}
      </Box>
    </Box>
  );
}

export default Display;
